/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2023 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2024 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2026 (c) Nico Brandt, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { useEffect, useState } from 'react';
import { Company, OpcUaClient, PutOpcUaClientReq, UserClient } from 'Api/generated';
import { Dispatch } from '@reduxjs/toolkit';
import { connect } from 'react-redux';
import MsgListActions, { BootstrapColor_t } from 'MsgList/MsgListActions';
import Api from 'Api/Api';
import { AllowedRoles, useAuth } from 'Contexts/Auth';
import Select from 'react-select';

interface IOpcUaClientProps {
  client: OpcUaClient;
  company: Company;
  clients: OpcUaClient[];
  onChange: () => void;
  availableUsers: UserClient[];
}

type DispatchProps_t = ReturnType<typeof mapDispatchToProps>;

export function OpcUaClientFunc({
  client,
  company,
  clients,
  onChange,
  availableUsers,
  AddMsg,
}: IOpcUaClientProps & DispatchProps_t) {
  const [edit, setEdit] = useState<boolean>(false);
  const [editClient, seteditClient] = useState<PutOpcUaClientReq>({
    Username: '',
    Comment: '',
  });
  const [assignedUsers, setAssignedUsers] = useState<UserClient[]>([]);
  const auth = useAuth();
  const isAdminOrSuperAdmin = auth.user?.Roles?.some((r) => r === 'SuperAdmin' || r === 'Admin') ?? false;

  useEffect(() => {
    if (isAdminOrSuperAdmin && client.Id) {
      Api.OpcUaClient.getClientUsers(client.Id)
        .then((res) => setAssignedUsers(res.data))
        .catch(() => console.error('Could not load client users'));
    }
  }, [client.Id, isAdminOrSuperAdmin]);

  const handleClientName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const _setedit = editClient;
    const key = event.target.name as keyof PutOpcUaClientReq;
    _setedit[key] = event.target.value;
    seteditClient({ ..._setedit });
  };

  const nameAlreadyExist = clients.find((c) => c.MqttUsername === editClient.Username) !== undefined;

  async function rmClient() {
    const id = client.Id;
    let confirmation_request = 'Are you sure you want to delete this client: ' + client.MqttUsername;

    if (window.confirm(confirmation_request) == true) {
      console.log('Remove the client ' + id + client.MqttUsername);
      confirmation_request = 'Deleted!';
      Api.OpcUaClient.deleteClient(id || '')
        .then(() => {
          AddMsg('Client removed from DB!', 'info');
          onChange();
        })
        .catch(() => {
          AddMsg('Network response was not ok', 'danger');
        });
    } else {
      confirmation_request = 'Canceled';
    }
  }

  //Save edited Data
  const SaveEditedData = async () => {
    Api.OpcUaClient.modifyClient(client.Id || '', editClient)
      .then(() => {
        onChange();
        setEdit(false);
        AddMsg('Saved changes in DB!', 'success ');
      })
      .catch(() => {
        AddMsg('Network response was not ok', 'danger');
      });
  };

  const groupedOptions = [
    {
      label: 'Company Users',
      options: availableUsers
        .filter((u) => u.CompanyIds?.includes(client.CompanyId || ''))
        .map((u) => ({ value: u.Id || '', label: u.Username || '' })),
    },
    {
      label: 'Other Users',
      options: availableUsers
        .filter((u) => !u.CompanyIds?.includes(client.CompanyId || ''))
        .map((u) => ({ value: u.Id || '', label: u.Username || '', isDisabled: true })),
    },
  ];

  return (
    <div className="row " key={client.Id}>
      <div className="form-group col-md-2 align-self-end">
        <label>MqttUserName</label>
        {edit && (
          <input
            className="form-control"
            name="Username"
            placeholder={client.MqttUsername || ''}
            onChange={(e) => handleClientName(e)}
            type="text"
          />
        )}
        {!edit && (
          <div>
            {company.ShortName}/{client.MqttUsername}
          </div>
        )}
      </div>
      <div className="form-group col-md-2 align-self-end">
        <label>Comment</label>
        {edit && (
          <input
            className="form-control"
            name="Comment"
            placeholder={client.Comment || ''}
            onChange={(e) => handleClientName(e)}
            type="text"
          />
        )}
        {!edit && <div>{client.Comment}</div>}
      </div>
      <div className="form-group col-md-3 align-self-end">
        <label>Client UUID</label>
        <label> {client.Id}</label>
      </div>
      <div className="row">
        <AllowedRoles roles={['SuperAdmin', 'Admin']}>
          <div className="form-group col-md-4 align-self-end">
            <label>Assigned Users</label>
            <Select
              closeMenuOnSelect={false}
              value={availableUsers
                .filter((u) => assignedUsers.some((a) => a.Id === u.Id))
                .map((u) => ({ value: u.Id || '', label: u.Username || '' }))}
              isMulti
              onChange={(e) => {
                const selected = availableUsers.filter((u) => e.some((opt) => opt.value === u.Id));
                setAssignedUsers(selected);
                // Direkt speichern bei Auswahl
                if (client.Id) {
                  Api.OpcUaClient.putClientUsers(
                    client.Id,
                    selected.map((u) => u.Id || ''),
                  )
                    .then(() => AddMsg('User assignments saved!', 'success '))
                    .catch(() => AddMsg('Could not save user assignments', 'danger'));
                }
              }}
              options={groupedOptions}
            />
          </div>
          <div className="form-group col-md-2 align-self-end ">
            <label> </label>
            <button className="btn btn-secondary float-end " onClick={() => rmClient()}>
              Remove Client
            </button>
          </div>
        </AllowedRoles>
        <div className="form-group col-md-3 align-self-end">
          <label> </label>
          <button className={edit ? 'btn btn-secondary ' : 'btn btn-primary'} onClick={() => setEdit(!edit)}>
            {!edit ? 'Edit Username' : 'Cancel'}
          </button>
          <label> </label>
          {edit && (
            <button
              className={
                !(nameAlreadyExist && client.MqttUsername != editClient.Username) ? 'btn btn-primary' : 'btn btn-danger'
              }
              disabled={
                (nameAlreadyExist && client.MqttUsername != editClient.Username) ||
                editClient.Username === '' ||
                editClient.Comment === ''
              }
              onClick={() => SaveEditedData()}
            >
              {!(nameAlreadyExist && client.MqttUsername != editClient.Username)
                ? 'Save changes'
                : 'Username already exits!'}
            </button>
          )}
        </div>
      </div>
      <div className="row border-bottom pb-2 mb-2 mx-0" key={client.Id}></div>
    </div>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    AddMsg: (msg: string, severity: BootstrapColor_t) => MsgListActions.Add(msg, severity)(dispatch),
  };
};

export default connect(undefined, mapDispatchToProps)(OpcUaClientFunc);
